import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import AnalyticsTracker from './components/AnalyticsTracker';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Specials = lazy(() => import('./pages/Specials'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Catering = lazy(() => import('./pages/Catering'));
const Weddings = lazy(() => import('./pages/Weddings'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const FormSubmitted = lazy(() => import('./pages/FormSubmitted'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/NotFound'));

const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminGallery = lazy(() => import('./pages/admin/GalleryManager'));
const AdminBlog = lazy(() => import('./pages/admin/BlogManager'));
const AdminBlogEdit = lazy(() => import('./pages/admin/BlogEdit'));
const AdminFAQ = lazy(() => import('./pages/admin/FAQManager'));
const AdminSubmissions = lazy(() => import('./pages/admin/Submissions'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminSpecialties = lazy(() => import('./pages/admin/SpecialtiesManager'));
const AdminSpecials = lazy(() => import('./pages/admin/SpecialsManager'));
const AdminWeddings = lazy(() => import('./pages/admin/WeddingsManager'));
const AdminInvoices = lazy(() => import('./pages/admin/InvoiceManager'));
const PayInvoice = lazy(() => import('./pages/PayInvoice'));
const Employment = lazy(() => import('./pages/Employment'));
const ApplicationSubmitted = lazy(() => import('./pages/ApplicationSubmitted'));
const AdminJobApplications = lazy(() => import('./pages/admin/JobApplications'));

function App() {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <AnalyticsTracker />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="specials" element={<Specials />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="catering" element={<Catering />} />
          <Route path="weddings" element={<Weddings />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="form-submitted" element={<FormSubmitted />} />
          <Route path="employment" element={<Employment />} />
          <Route path="application-submitted" element={<ApplicationSubmitted />} />
        </Route>

        {/* Invoice Payment (standalone page, no layout) */}
        <Route path="/pay/:id" element={<PayInvoice />} />
        <Route path="/pay/:id/success" element={<PayInvoice />} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/gallery"
          element={
            <ProtectedRoute>
              <AdminGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog"
          element={
            <ProtectedRoute>
              <AdminBlog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/new"
          element={
            <ProtectedRoute>
              <AdminBlogEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/edit/:id"
          element={
            <ProtectedRoute>
              <AdminBlogEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/faq"
          element={
            <ProtectedRoute>
              <AdminFAQ />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/submissions"
          element={
            <ProtectedRoute>
              <AdminSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute>
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/specialties"
          element={
            <ProtectedRoute>
              <AdminSpecialties />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/specials"
          element={
            <ProtectedRoute>
              <AdminSpecials />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/weddings"
          element={
            <ProtectedRoute>
              <AdminWeddings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <ProtectedRoute>
              <AdminInvoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/job-applications"
          element={
            <ProtectedRoute>
              <AdminJobApplications />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
